import { Edit, Visibility } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Grid, IconButton, useEventCallback } from '@mui/material';
import { memo, ReactElement, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { noop } from 'shell/utils/noop';
import { generatePath, useNavigate } from 'react-router-dom';
import { OperationObject } from 'system/utils/event-batch/filters-object-to-query-string-batch-event';
import { ActListVM } from 'civil-status/view-models/research-form';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { STATES_ACT } from 'civil-status/constants/states-act';
import DeleteDialog from 'shell/components/shared/delete-dialog';

interface TableActProps {
  data?: Array<ActListVM>;
  filterSelected?: OperationObject;
  getActListData: (data: OperationObject, page?: number, rowsPerPage?: number) => void;
  tableProps?: Record<string, number>;
  setTableProps: (values: Record<string, number>) => void;
}

const TableActComponent = ({
  data,
  filterSelected,
  getActListData,
  tableProps = {},
  setTableProps,
}: TableActProps): ReactElement => {
  const navigate = useNavigate();

  const [elementToDelete, setElementToDelete] = useState<ActListVM | undefined>(undefined);

  const onEditPress = useEventCallback((item: ActListVM) => {
    if (!item.idAct) {
      return;
    }

    navigate({
      pathname: generatePath('/stato-civile/ricerca-atto-da-compilare/modifica/:idEvento', {
        idEvento: item.idAct,
      }),
    });
  });

  const onVisibilityPress = useEventCallback((item: ActListVM) => {
    if (!item.idAct) {
      return;
    }

    navigate({
      pathname: generatePath('/stato-civile/ricerca-atto-da-compilare/visualizza/:idEvento', {
        idEvento: item.idAct,
      }),
    });
  });

  const deleteData = useHttpClient(``, {
    options: {
      method: 'DELETE',
      loading: true,
      errorCatching: true,
    },
  });

  const clearToDelete = useEventCallback(() => {
    // pulire lo stato
    setElementToDelete(undefined);
  });

  const onTrashPress = useEventCallback(() => {
    if (!elementToDelete?.idAct) {
      return;
    }

    (
      deleteData({
        url: `${__ACT_API_URL__}/bozza/rimuovi/${elementToDelete?.idAct}`,
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      // pulire lo stato
      clearToDelete();
      // ricaricare gli atti
      getActListData(filterSelected || {}, tableProps.page, tableProps.rowsPerPage);
    });
  });

  const body: Array<ActListVM> = useMemo(() => {
    return (data || []).map((item) => {
      return {
        ...item,
        action: (
          <div className="pmi-d-flex">
            {item.idState === STATES_ACT.ID_STATE_FIRMA_COMPLETA_USC ? (
              <IconButton
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onVisibilityPress(item);
                }}
                aria-label="Visualizza elemento"
                aria-haspopup="true"
                style={{
                  padding: '4px',
                }}
              >
                <Visibility />
              </IconButton>
            ) : (
              <IconButton
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onEditPress(item);
                }}
                aria-label="Modifica elemento"
                aria-haspopup="true"
                style={{
                  padding: '4px',
                }}
                disabled={item.idState === STATES_ACT.ID_STATE_ANNULLATO}
              >
                <Edit />
              </IconButton>
            )}
            <IconButton
              color="error"
              aria-label="elimina"
              component="span"
              onClick={() => setElementToDelete(item)}
              disabled={
                item.idState === STATES_ACT.ID_STATE_ANNULLATO ||
                item.idState === STATES_ACT.ID_STATE_FIRMA_COMPLETA_USC
              }
            >
              <DeleteIcon />
            </IconButton>
          </div>
        ),
      };
    });
  }, [data]);

  const onChangePage = useEventCallback((page: number) => {
    getActListData(filterSelected || {}, page, tableProps.rowsPerPage);

    setTableProps({
      page,
      rowsPerPage: tableProps.rowsPerPage,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    getActListData(filterSelected || {}, 0, rowsPerPage);

    setTableProps({
      page: 0,
      rowsPerPage,
    });
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Table
          ariaLabel="Lista pratiche"
          page={tableProps.page || 0}
          rowsPerPage={tableProps.rowsPerPage || 10}
          body={body}
          count={(body && body[0]?.totalCount) || 0}
          head={headCells}
          onSort={noop}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          orders={{}}
          isNotLayoutFixed
          noSort
          hideButtons
          isServer
        />
      </Grid>
      {Boolean(elementToDelete) ? (
        <DeleteDialog
          isOpen={Boolean(elementToDelete)}
          onAbort={clearToDelete}
          onClose={clearToDelete}
          onConfirm={onTrashPress}
          text="Sei sicuro di voler eliminare la bozza?"
        />
      ) : null}
    </Grid>
  );
};

TableActComponent.displayName = 'Practice Research - Table Persons component';

const TableAct = memo(TableActComponent);

export default TableAct;
